/**
 * Zustand Store Types
 *
 * Single source of truth for all state types used across stores.
 * Replaces hooks/useBashSocket.types.ts with only the types that are actually used.
 */

import type { TaskOutputDoc } from "@bash/shared";
import type {
  ContentBlock,
  QuestionBlock,
  ImageAttachment,
  ToolUseBlock,
  TodoBlock,
  Todo,
  ToolSummary,
} from "@/types";
import type {
  TaskOutput,
  TaskTodoGroup,
  DisplayStatus,
  MessageIdentity,
  PendingDetails,
  ContextUsageSnapshot,
  MemoryRecallEntry,
  HarnessId,
  SubagentPathSegment,
  SubagentId,
  PermissionSuggestion,
  ToolPermissionDetail,
  ToolPermissionReason,
  AccessLevel,
  LivePreviewPointer,
  TaskPreviewReadyPayload,
  OriginChannel,
  PlanLimitRequest,
} from "@bash/shared";

// =============================================================================
// Constants
// =============================================================================

/** The default agent slug used when no specific agent is provided. */
export const DEFAULT_AGENT = "bash";

// =============================================================================
// Core Types
// =============================================================================

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: ContentBlock[];
  timestamp: Date;
  agentSlug: string;
  sendFailed?: boolean;
  /** Why the server could not record this send: its ack code, never its text
   *  (NotRecordedCode, stores/handlers/emitUserMessage.ts). */
  sendFailedCode?: "message_blocked" | "message_not_recorded";
  /**
   * SDK assistant-message uuid. Set from AGENT_END for assistant messages.
   *
   * Also mirrored on `identity.sdkMessageUuid` for persisted messages so
   * lookups by any id kind work through `findMessageByAnyId`.
   */
  sdkMessageUuid?: string;
  /**
   * Canonical identity for this message. See `MessageIdentity` in
   * @bash/shared for the reconciliation model.
   *
   * Populated at creation time for every new message and reconciled
   * optimistic → persisted at MESSAGE_COMPLETE / AGENT_END. Readers
   * should go through `findMessageByAnyId` / `getMessageCanonicalId`
   * instead of comparing `.id` directly — those helpers match across
   * all three id kinds (client, server, sdk uuid) in one pass.
   *
   * Left optional for defense-in-depth during the rollout: a few legacy
   * test helpers and serialized rows may not populate it. Readers tolerate
   * absence by falling back to the `id` field.
   */
  identity?: MessageIdentity;
  /**
   * RENDER_FROM_TRUTH — per-row DB insert-order sequence (`messages.seq`),
   * stamped when the row is adopted from a MESSAGE_SYNC payload. Absent on
   * client-only rows (optimistic sends, markers), which keep their relative
   * position via `orderBySeq`'s gluing rule. Unset when the flag is off.
   */
  seq?: number;
  /**
   * RENDER_FROM_TRUTH overlay-only — the server row id announced by
   * AGENT_END (`data.messageId`). The overlay dies when a committed row with
   * this id arrives; never set on committed rows. Unset when the flag is off.
   */
  expectedServerId?: string;
  /**
   * RENDER_FROM_TRUTH — set when an ORPHAN overlay (interrupt/stop end, no
   * messageId announced) is promoted into the committed list by a follow-up
   * AGENT_START. Adoption drops the row once a never-seen assistant row
   * arrives (its DB flush landed) so it can't duplicate its own flushed copy.
   * Unset when the flag is off.
   */
  awaitingEcho?: boolean;
}

/**
 * TaskPhase - single source of truth for task state.
 *
 * Replaces 3 independent fields (status, lifecycle, isWaitingForResponse)
 * with one discriminant, making partial-update bugs impossible.
 */
export type TaskPhase = "idle" | "processing" | "waiting" | "interrupting";

// =============================================================================
// Unified Pending Interaction (discriminated union)
// =============================================================================

interface PendingInteractionBase {
  requestId: string;
  toolUseId: string;
  agentSlug: string;
  taskId: string;
  /**
   * Input Station interaction id. Present when the pending was created via
   * the new INTERACTION_REQUIRED path; absent on legacy CONNECTION_REQUIRED
   * / PENDING_QUESTION sources. Response emits INTERACTION_RESPONSE when
   * present, otherwise falls back to legacy events.
   */
  interactionId?: string;
  /**
   * Subagent-deep-HITL (T9) — OPTIONAL subagent identity that raised this
   * interaction. `subagentId` = the leaf `Agent` tool_use id that owns the
   * pause; `subagentPath` = the root→leaf attribution path. BOTH ABSENT ⇒ a
   * main-agent interaction ⇒ byte-identical to today (lives in
   * `TaskState.pendingInteraction`, drives the global InputStation). Present ⇒
   * the pause is routed to `TaskState.pendingByLane[subagentId]` and rendered
   * inline in its DelegationCard lane, blocking only that lane.
   */
  subagentPath?: SubagentPathSegment[];
  subagentId?: SubagentId;
}

export interface PendingQuestionInteraction extends PendingInteractionBase {
  type: "question";
  questions: QuestionBlock["questions"];
  /**
   * Browser handoff (spec 2026-09-02): what the ask IS and WHERE the person
   * acts. `intent: 'handoff'` = they must do or hand over something (the
   * handoff kind); `surface` = in the browser Bash is driving, or on their
   * phone; `url` + `frame` = the page and how it looked when Bash asked; `why`
   * = Bash's one quiet line. All absent on a classic AskUserQuestion row.
   */
  intent?: "handoff" | "question";
  surface?: "browser" | "phone";
  url?: string;
  frame?: string;
  why?: string;
  /**
   * File answers (spec 2026-09-24): HOW the person answers. "file" = the card
   * leads with Attach file. Absent (or "text") = today's typed answer.
   */
  answer?: "text" | "file";
}

export interface PendingApprovalInteraction extends PendingInteractionBase {
  type: "approval";
  toolName: string;
}

export interface PendingPermissionInteraction extends PendingInteractionBase {
  type: "permission";
  toolName: string;
  toolInput: unknown;
  /**
   * Plain-language one-liner of what the call will do ("Delete 2 files"),
   * computed sandbox-side (describeToolCall). The card leads with it and
   * demotes the raw command to a disclosure. Absent ⇒ the neutral
   * "Allow this step?" (permissionHeadline), never the tool's name.
   */
  summary?: string;
  /**
   * Toolkit slug for the card's brand icon ("gmail", "github", …), set
   * sandbox-side when a single Composio app names the call. Absent ⇒ the card
   * shows its generic warning glyph.
   */
  appSlug?: string;
  /**
   * SDK-supplied rules the user can accept via "Always allow" to auto-approve
   * this tool family. Absent/empty ⇒ the "Always allow" button is hidden.
   */
  suggestions?: PermissionSuggestion[];
  /**
   * Rich permission-card fields (describePermissionAsk, computed sandbox-side):
   * label/value detail rows, the action-naming button label, and why-the-ask
   * (family + floor + why-line). ALL absent on legacy bundles ⇒ the card renders
   * exactly today's summary-only form. `reason.family ∈ {dangerous_delete,
   * protected_remote, payment}` is what flips the primary button to the red tone.
   */
  details?: ToolPermissionDetail[];
  actionLabel?: string;
  reason?: ToolPermissionReason;
}

export interface PendingAuthInteraction extends PendingInteractionBase {
  type: "auth";
  toolkit: string;
  displayName?: string;
  authUrl?: string;
  /** Multi-account (runner emits from PR 2; absent = today's ask). Which
   *  account the agent needs — a Composio alias or account id. */
  accountHint?: string;
  /** 'add' marks an ask that deliberately wants ANOTHER account while a
   *  sibling is ACTIVE. Connect MUST mint with mode:'add' — a plain connect
   *  answers "Already connected" with no URL. */
  mode?: "add";
}

export interface PendingCreditInteraction extends PendingInteractionBase {
  type: "credit";
  toolName: string;
  estimatedCostUsd: number;
  balanceUsd: number;
}

export interface PendingPlanInteraction extends PendingInteractionBase {
  type: "plan";
  plan: string;
}

export interface PendingCustomInteraction extends PendingInteractionBase {
  type: "custom";
  /** Renderer key matching a registered FE component. */
  renderer: string;
  /** User-facing card title. */
  title?: string;
  /** Card subtitle / description. */
  description?: string;
  /** Renderer-specific config. Treated as opaque by gateway/server/DB. */
  config: Record<string, unknown>;
  submitLabel?: string;
  allowSkip?: boolean;
  skipLabel?: string;
}

/**
 * OS Hands P2.5a — a held computer-use consent ask (kind 'hands_approval',
 * minted server-side by the hands gate). Two surfaces ride this one variant,
 * keyed by `firstEngage` + `lane`: the one-time OS lane-on card, and the
 * per-app / browser ask. Carries only the gate's projected fields — the card's
 * copy is DERIVED from them (interactions/handsApproval.ts), no display strings
 * on the wire. `nonce` is the single-use token echoed back on approve.
 */
export interface PendingHandsInteraction extends PendingInteractionBase {
  type: "hands";
  /** 'os' (Mac apps) or 'browser'. Absent ⇒ browser (legacy rows). */
  lane?: string;
  /** One-time OS lane-on consent. Names no app; per-action fields absent. */
  firstEngage?: boolean;
  /** OS lane — macOS bundle id, e.g. 'com.apple.mail'. */
  app?: string;
  /** Browser lane — the origin, standing in for the app. */
  url?: string;
  /** OS lane — the disjoint consent capability: 'actuation' | 'pixels'. */
  capability?: string;
  tool?: string;
  mode?: string;
  label?: string;
  tier?: "low" | "medium" | "high";
  /** Single-use action-bound token echoed into the allow outcome. */
  nonce: string;
}

/**
 * Engines T5 — the task is paused on the person's own used-up plan (kind
 * 'plan_limit', SERVER-minted: no box registered it). Carries the whole
 * request, so the card draws its options, the reset and the default exactly as
 * minted. `interactionId` is `request.id`.
 */
export interface PendingPlanLimitInteraction extends PendingInteractionBase {
  type: "plan_limit";
  request: PlanLimitRequest;
  /**
   * The server refused the last tap and the card stays pending: 'no_credits'
   * (409) is said under the Vestra row. Set only by a failed resolve.
   */
  refused?: "no_credits";
}

export type PendingInteraction =
  | PendingQuestionInteraction
  | PendingApprovalInteraction
  | PendingPermissionInteraction
  | PendingAuthInteraction
  | PendingCreditInteraction
  | PendingPlanInteraction
  | PendingCustomInteraction
  | PendingHandsInteraction
  | PendingPlanLimitInteraction;

/**
 * Convert server-side pendingDetails (stored in DB) to frontend PendingInteraction.
 */
/**
 * The browser-handoff words an ask_question row may carry (spec 2026-09-02),
 * and how the person answers (`answer`, spec 2026-09-24; only "text" | "file"),
 * cherry-picked so an absent field stays absent (a classic question's shape is
 * byte-identical to before). Shared by the live parse and the reload path.
 */
export function pickHandoffFields(
  src: Record<string, unknown>
): Pick<
  PendingQuestionInteraction,
  "intent" | "surface" | "url" | "frame" | "why" | "answer"
> {
  const out: Pick<
    PendingQuestionInteraction,
    "intent" | "surface" | "url" | "frame" | "why" | "answer"
  > = {};
  if (src.intent === "handoff" || src.intent === "question")
    out.intent = src.intent;
  if (src.surface === "browser" || src.surface === "phone")
    out.surface = src.surface;
  if (typeof src.url === "string" && src.url) out.url = src.url;
  if (typeof src.frame === "string" && src.frame) out.frame = src.frame;
  if (typeof src.why === "string" && src.why) out.why = src.why;
  if (src.answer === "text" || src.answer === "file") out.answer = src.answer;
  return out;
}

export function serverDetailsToPendingInteraction(
  taskId: string,
  agentSlug: string,
  details: { reason: string; [key: string]: unknown }
): PendingInteraction | null {
  const interactionId =
    (details.interactionId as string | undefined) || undefined;
  // Subagent-deep-HITL (T9) — the DB row carries `subagent_id` / `subagent_path`
  // columns (T2). Stamp them so a reconnect re-renders the pause in its lane.
  // Absent ⇒ a main-agent interaction (byte-identical to today).
  const subagentId =
    (details.subagent_id as string | undefined) ??
    (details.subagentId as string | undefined) ??
    undefined;
  const subagentPath =
    (details.subagent_path as SubagentPathSegment[] | undefined) ??
    (details.subagentPath as SubagentPathSegment[] | undefined) ??
    undefined;
  const base = {
    taskId,
    agentSlug,
    requestId: (details.requestId as string) || "",
    toolUseId: (details.toolUseId as string) || "",
    ...(interactionId && { interactionId }),
    ...(subagentId && { subagentId }),
    ...(subagentPath && { subagentPath }),
  };
  switch (details.reason) {
    case "question":
      return {
        ...base,
        type: "question",
        questions: (details.questions as QuestionBlock["questions"]) || [],
        ...pickHandoffFields(details),
      };
    case "approval": {
      // ExitPlanMode interactions carry `plan` — promote to the `plan` variant
      // so ExitPlanInput renders the plan text.
      const plan = details.plan as string | undefined;
      if (plan !== undefined) {
        return { ...base, type: "plan", plan };
      }
      return {
        ...base,
        type: "approval",
        toolName: (details.toolName as string) || "ExitPlanMode",
      };
    }
    case "permission": {
      // Rich card fields (describePermissionAsk) on the reload path. details /
      // actionLabel / appSlug / suggestions ride top-level (no key collision);
      // the reason OBJECT is read ONLY from a nested persisted request —
      // `details.reason` here is the pending KIND discriminator ('permission'),
      // never the ToolPermissionReason. The server projects all of them
      // (pending-details-projection.ts; #bugs 2026-09-25). Absent on an older row
      // ⇒ the neutral headline.
      const persistedRequest = details.request as
        { reason?: unknown } | undefined;
      return {
        ...base,
        type: "permission",
        toolName: (details.toolName as string) || "",
        toolInput: details.toolInput,
        ...(typeof details.summary === "string" && {
          summary: details.summary,
        }),
        ...(Array.isArray(details.details) && {
          details: details.details as ToolPermissionDetail[],
        }),
        ...(typeof details.actionLabel === "string" && {
          actionLabel: details.actionLabel,
        }),
        ...(typeof details.appSlug === "string" &&
          details.appSlug && { appSlug: details.appSlug }),
        ...(Array.isArray(details.suggestions) && {
          suggestions: details.suggestions as PermissionSuggestion[],
        }),
        ...(typeof persistedRequest?.reason === "object" &&
          persistedRequest?.reason !== null && {
            reason: persistedRequest.reason as ToolPermissionReason,
          }),
      };
    }
    case "auth": {
      const authResult: PendingAuthInteraction = {
        ...base,
        type: "auth",
        toolkit: (details.integration as string) || "",
      };
      if (typeof details.displayName === "string")
        authResult.displayName = details.displayName;
      if (typeof details.authUrl === "string")
        authResult.authUrl = details.authUrl;
      if (typeof details.accountHint === "string")
        authResult.accountHint = details.accountHint;
      if (details.mode === "add") authResult.mode = "add";
      return authResult;
    }
    case "custom": {
      // Phase 8: custom-kind interactions from the new request_user_input MCP tool.
      // `details` comes from the server's pendingDetails shape, which mirrors
      // the CustomInteractionRequest fields persisted in the DB.
      // If the server doesn't yet persist renderer-specific fields, fall back to
      // the `interactions.request` object (which IS persisted via registerInteraction).
      const request = (details.request ?? {}) as Record<string, unknown>;
      return {
        ...base,
        type: "custom",
        interactionId: (details.interactionId as string) || "",
        renderer:
          (details.renderer as string) ||
          (request.renderer as string) ||
          "unknown",
        title:
          (details.title as string | undefined) ??
          (request.title as string | undefined),
        description:
          (details.description as string | undefined) ??
          (request.description as string | undefined),
        config:
          (details.config as Record<string, unknown>) ??
          (request.config as Record<string, unknown>) ??
          {},
        submitLabel:
          (details.submitLabel as string | undefined) ??
          (request.submitLabel as string | undefined),
        allowSkip:
          (details.allowSkip as boolean | undefined) ??
          (request.allowSkip as boolean | undefined),
        skipLabel:
          (details.skipLabel as string | undefined) ??
          (request.skipLabel as string | undefined),
      } satisfies PendingCustomInteraction;
    }
    case "credit":
      return {
        ...base,
        type: "credit",
        toolName: (details.toolName as string) || "",
        estimatedCostUsd: (details.estimatedCostUsd as number) ?? 0,
        balanceUsd: (details.balanceUsd as number) ?? 0,
      } satisfies PendingCreditInteraction;
    case "hands": {
      // OS Hands P2.5a — rehydrate the consent card on reconnect / navigate-in.
      // The gate registers a rich request; the server projects it into these
      // fields (projectRequestToPendingDetails). Fall back to the persisted
      // `interactions.request` object for any field the projection omits.
      const request = (details.request ?? {}) as Record<string, unknown>;
      const pick = <T>(k: string): T | undefined =>
        (details[k] as T | undefined) ?? (request[k] as T | undefined);
      const handsResult: PendingHandsInteraction = {
        ...base,
        type: "hands",
        nonce: pick<string>("nonce") ?? "",
      };
      const lane = pick<string>("lane");
      if (lane !== undefined) handsResult.lane = lane;
      const firstEngage = pick<boolean>("firstEngage");
      if (firstEngage !== undefined) handsResult.firstEngage = firstEngage;
      const app = pick<string>("app");
      if (app !== undefined) handsResult.app = app;
      const url = pick<string>("url");
      if (url !== undefined) handsResult.url = url;
      const capability = pick<string>("capability");
      if (capability !== undefined) handsResult.capability = capability;
      const tool = pick<string>("tool");
      if (tool !== undefined) handsResult.tool = tool;
      const mode = pick<string>("mode");
      if (mode !== undefined) handsResult.mode = mode;
      const label = pick<string>("label");
      if (label !== undefined) handsResult.label = label;
      const tier = pick<"low" | "medium" | "high">("tier");
      if (tier !== undefined) handsResult.tier = tier;
      return handsResult;
    }
    case "plan_limit": {
      // Engines T5 — the reload projection is { reason, interactionId, request }
      // (PendingPlanLimitDetails). The card is its request: a row without one
      // draws no options, so it rebuilds no card at all.
      const request = details.request as PlanLimitRequest | undefined;
      if (!request || typeof request !== "object") return null;
      return {
        ...base,
        type: "plan_limit",
        request,
        interactionId: interactionId ?? request.id,
        requestId: base.requestId || request.id,
        toolUseId: base.toolUseId || request.id,
      } satisfies PendingPlanLimitInteraction;
    }
    default:
      return null;
  }
}

// =============================================================================
// Task State
// =============================================================================

/**
 * P5.4 (Agent Teams) — identity marker on a HIDDEN teammate TaskState. A
 * teammate thread is a REAL TaskState in the tasks Map (keyed by its own
 * teammateTaskId) so the entire per-task machinery (streaming handlers, phase,
 * pendingInteraction, MESSAGE_SYNC reload) works unchanged — but it renders
 * EMBEDDED under the parent's TeammateCard and must NEVER surface in
 * sidebars/task lists/persistence (every list surface gates on this field).
 */
export interface TeammateMeta {
  /** Unique-per-parent handle, e.g. 'researcher' (the @name routing key). */
  name: string;
  parentTaskId: string;
  /** The parent's Agent tool_use block that spawned this teammate. Empty
   *  string when the lane was minted from a message that raced the spawn
   *  event — upgraded in place when the real spawn/marker arrives. */
  parentToolUseId: string;
  agentSlug: string;
  agentIcon?: string;
  agentColor?: string;
  /**
   * Phase 2 (background helpers) — 'helper' = a depth-0 one-shot spawned to work
   * in the background; absent/'teammate' = a named teammate. Absent on every
   * pre-Phase-2 lane, so the teammate shape is unchanged with the flag off.
   */
  kind?: "teammate" | "helper";
  /** Phase 2 — the Agent call's `description`. A helper has no handle the person
   *  chose, so this is what its lane is titled with. Carried by BOTH sources since
   *  R76: the live `subagent:spawned` event and the delegated marker a reload
   *  rebuilds from, so only a lane predating R76 falls back to the minted handle. */
  description?: string;
  /**
   * Phase 3 (spec 2026-09-05 §4.6, D8) — the person can message this helper, so the
   * composer's @ list offers it. Set from the spawn event or the delegated marker,
   * both of which carry it only under the team-surface flag; a teammate never carries
   * it (its own name has always been addressable). The @ list reads THIS field and
   * never a handle's shape, so a Phase 2 helper stays out of it.
   */
  addressable?: boolean;
}

/**
 * Which user-send path armed the perceived-TTFT clock — kept as a property on
 * the `task:first_token` event so latency can be segmented by entry point.
 */
export type PerceivedSendPath = "new_task" | "continuation" | "teammate";

/**
 * Client-perceived TTFT measurement armed on a user send. Transient (never
 * persisted); see stores/helpers/perceivedTtft.ts.
 */
export interface PerceivedTtftState {
  /** Epoch ms of the send (the moment delegation:submitted fired). */
  sentAt: number;
  sendPath: PerceivedSendPath;
  /** The send's end-to-end trace id, echoed onto the first-token event. */
  traceId: string;
}

/**
 * Ambient "hands active" signal for the OS-hands lane — the agent is operating
 * the user's real Mac (rung 3/4 takeover holds the physical cursor/keyboard;
 * background actuation drives AX/postToPid). Written by osHandsHandler from the
 * OS_HANDS_ACTIVE socket event and read by AgentStatusLine. Transient (never
 * persisted — absent from persistence.ts's serialized allowlist); an inactive
 * payload clears the field.
 */
export interface HandsActiveState {
  active: boolean;
  /** Normalized bundle id being driven, when known (e.g. 'com.apple.mail'). */
  app?: string;
  /** Actuation rung: 1/2 background, 3 HID cursor/keys, 4 vision-coordinate. */
  rung?: number;
  /** True while the REAL cursor/keyboard are held (a takeover grant window). */
  takeover: boolean;
}

/**
 * Live Localhost Preview presence — set while a task's dev server is surfaced as
 * a live iframe, cleared (undefined) when it retracts. Ephemeral: absent from
 * persistence.ts's serialized allowlist, so it never survives a reload — a stale
 * live URL 502s once the sandbox sleeps. The ready payload's own fields (url,
 * token, port, caption, entry path, a boat link's tokenParam), less its task.
 */
export type LivePreviewState = Omit<TaskPreviewReadyPayload, "taskId">;

export interface TaskState {
  taskId: string;
  messages: Message[];
  streamingMessage: Message | null;
  phase: TaskPhase;
  error?: string;
  /** No-access belief (founder 08-04); own stream or a good sync clears it. */
  accessDenied?: boolean;
  /** Epoch ms THIS client minted the id; an early TASK_NOT_FOUND = not yet. */
  mintedHereAt?: number;
  /** A successful MESSAGE_SYNC has vouched for this task at least once. Until
   *  then a zero-message deep link renders quiet loading, never the fabricated
   *  empty "Done" conversation. */
  hasSynced?: boolean;
  /**
   * Per-task revision (P3.4) last adopted from a server MESSAGE_SYNC. When both
   * this and an incoming sync carry a seq, the sync handler prefers a revision
   * compare (adopt iff newer, skip iff older/equal) over the legacy length/
   * wall-clock staleness heuristics. Undefined until the first seq-bearing sync
   * (or on a legacy server) ⇒ heuristics apply, byte-identical to pre-P3.4.
   */
  seq?: number;
  title: string;
  createdAt: Date;
  lastActivityAt: Date;
  /**
   * Main-agent (`''` lane) pending interaction. Back-compat home: every existing
   * call site reads/writes this for the main agent, so a main-agent pause is
   * byte-identical to today (drives the global InputStation, attention chrome,
   * `useSelectedTaskPendingInteraction`).
   */
  pendingInteraction: PendingInteraction | null;
  /**
   * What the task was waiting for, from the moment its ask settles until the
   * turn reopens (AGENT_START). Between those two the task is not processing and
   * nothing is pending, and every surface used to read that as DONE — the founder
   * connected GitHub and watched "Bash is done" for a minute (prod ef636b38,
   * 09-03). The line reads this instead: "verifying the connection", "picking
   * up your answer". Null whenever the task is not in that gap.
   */
  resumingFrom?: PendingInteraction["type"] | null;
  /**
   * Main-agent ask QUEUE (FIFO tail) — siblings of `pendingInteraction` from a
   * parallel tool batch that minted several pauses in the same instant.
   * `pendingInteraction` is always the visible HEAD; settling it promotes the
   * next entry from here. Non-empty queue ⇒ non-null head. Mutate ONLY through
   * the helpers in `stores/pendingQueue.ts`. Empty [] for the common
   * single-pause case.
   */
  pendingQueue: PendingInteraction[];
  /**
   * Subagent-deep-HITL (T9) — per-lane pending interactions for SUBAGENT pauses,
   * keyed by `subagentId` (the leaf `Agent` tool_use id). A subagent pause is
   * routed here (NOT to `pendingInteraction`) so the global InputStation stays in
   * text mode and only the matching DelegationCard lane blocks; independent lanes
   * keep streaming. The main agent's `''` lane lives in `pendingInteraction`
   * above — read either via `useLanePendingInteraction('')`. Empty `{}` for the
   * common (no-subagent-pause) case.
   */
  pendingByLane: Record<SubagentId, PendingInteraction>;
  agentSlug: string;
  /**
   * Agents first (1c): the durable agent this task runs as, from the server DTO (tasks:list /
   * MESSAGE_SYNC). null or undefined = Bash. `agentSlug` above is the runtime lane and never
   * says who the agent is. The story draws the agent's face from this and nothing else.
   */
  agentId?: string | null;
  /**
   * Harness runtime (engine) this task is locked to — claude | openai. Set at
   * creation from the global engine selector, hydrated from the server
   * (tasks:list / MESSAGE_SYNC), and never changed afterward. The composer's
   * engine selector renders read-only from this when a task is active.
   */
  runtime: HarnessId;
  /**
   * Literal model id pinned per task by the model picker (§4.7). Set at creation
   * from the model selector, hydrated from the server (MESSAGE_SYNC / tasks:list)
   * and persistence. Undefined ⇒ legacy task → server resolves via resolveAgentModel.
   * The composer's ModelSelector renders read-only from this when a task is active.
   */
  model?: string;
  /**
   * Effective access level for the task-header picker (permissions v2). Hydrated
   * from the server DTO (tasks:list / MESSAGE_SYNC). NULL / undefined = the task
   * inherits the user default (the picker renders a "(default)" marker).
   */
  accessLevel?: AccessLevel | null;
  // Per-task token tracking (not global — parallel tasks don't bleed)
  tokenUsage: TokenUsage;
  turnSummary: TurnSummary | null;
  turnStartTime: number | null;
  /** Epoch ms of the current turn's AGENT_START — the visible timer's anchor.
   *  Null until the agent runner actually starts (boot time excluded by
   *  construction) and outside active turns. */
  agentStartedAt: number | null;
  /**
   * Epoch ms at which THIS client watched the runner's own AGENT_END seal a
   * turn — first-hand terminal knowledge, fresher than any server row read.
   * Cleared the moment a turn re-opens (AGENT_START / a send), so it only ever
   * marks "the last thing I saw was the end". A MESSAGE_SYNC that still claims
   * 'processing' within the trust window is stale and must not re-arm the
   * footer loader (founder 08-20, task 54510a8b: 35s of spinner after Stop).
   * Transient — never persisted; a rehydrated page has no first-hand knowledge.
   */
  turnSealedAt?: number | null;
  /**
   * TRUE iff the last terminal this client saw for the task was a CUT-SHORT one: an
   * AGENT_END whose footer says interrupted, or the server's own `user_interrupt`
   * stop. Cleared the moment a turn re-opens, like `turnSealedAt`. Read by the helper
   * lane's ending line (C1): a helper whose report never reached this client would
   * otherwise read "Helper finished" for a run the person killed. Transient.
   */
  turnEndedInterrupted?: boolean;
  /** Several accounts: the exact resume_at a switch to the next account armed as its safety net (planLimitNetAt, from the switched AGENT_END
   *  or, on a sync, the newest switched footer). A resumeAt that IS this instant is never a wait (isSwitchNet); any other one is. */
  switchNetAt?: string | null;
  /**
   * Client-perceived TTFT — armed the instant a user send fires (one of the 3
   * send paths), cleared when the first agent streaming delta renders for this
   * task. Transient (never persisted); null/undefined outside an in-flight
   * measured send. See stores/helpers/perceivedTtft.ts.
   */
  perceivedTtft?: PerceivedTtftState | null;
  /** Persisted task outputs from DB (latest first) */
  outputs: TaskOutput[];
  /** Persisted todo groups from DB (latest first) */
  todos: TaskTodoGroup[];
  /** Prompt suggestion from agent (shown as chip below message input) */
  promptSuggestion: string | null;
  /** SDK system status (e.g. 'compacting' during context optimization) */
  sdkStatus?: string | null;
  /**
   * OS-hands lane presence — set while the agent is operating the user's Mac,
   * cleared (undefined) when it stops. Ephemeral; drives AgentStatusLine's
   * "Acting in <App>" line (priority over the derived working label).
   */
  handsActive?: HandsActiveState;
  /** Live Localhost Preview frame — set on task:preview_ready, cleared on
   *  task:preview_closed or when the sandbox hibernates/errors. Its lifetime is
   *  the SANDBOX being awake, not the turn: a finished turn leaves the dev
   *  server running and the url answering. Ephemeral (never persisted): the
   *  url+token inside it 502 once the sandbox sleeps. */
  livePreview?: LivePreviewState;
  /**
   * DURABLE live-preview pointer (tasks.live_preview), hydrated from the server
   * DTO (MESSAGE_SYNC / tasks:list). Non-null = this task has an app on the
   * sandbox's reserved port that the user can wake, even after the turn ended
   * and even after a reload. Carries no url/token by construction — those are
   * minted per wake. Separate field from `livePreview` above precisely because
   * one dies when the sandbox sleeps and the other does not.
   */
  livePreviewPointer?: LivePreviewPointer | null;
  /**
   * R-A channel provenance, hydrated from the server DTO (tasks:list on a
   * sidebar open, MESSAGE_SYNC on a deep link or cold reload). Absent =
   * app-born; the task header's "via <channel>" chip reads this and nothing
   * else. Immutable for the life of the task — it names a birth, so no
   * streaming event ever rewrites it.
   */
  originChannel?: OriginChannel;
  /**
   * Engines T5 (tasks.resume_at, ISO): the task is paused on the person's own
   * used-up plan and picks up by itself at this moment. Null/absent = not
   * waiting on a plan. Hydrated from the list row, MESSAGE_SYNC and TASK_STREAM;
   * the server clears it when the pause ends (a turn starts, a switch lands).
   */
  resumeAt?: string | null;
  /** Several accounts (tasks.engine_slot_id): the slot of the task's last Claude Code turn, which the header names; null = none recorded (the primary). Hydrated like resumeAt. */
  engineSlotId?: string | null;
  /** Active API retry (rate limit / overload visibility) */
  retryState?: {
    attempt: number;
    maxRetries: number;
    retryDelayMs: number;
    errorStatus: number | null;
    startedAt: number;
  } | null;
  /**
   * Latest context-window snapshot, updated each turn via AGENT_CONTEXT_USAGE.
   * Consumed by the task menu's Usage rows (TaskHeaderBar).
   */
  contextUsage?: ContextUsageSnapshot;
  /**
   * Accumulated memory_recall events for this task — each entry is one
   * AGENT_MEMORY_RECALL event the agent emitted while pulling from the
   * user's memory store. NO UI reads this today: the header pill was removed
   * 2026-09-15 (founder). The stream is kept because the pick is still
   * announced and still persisted with the turn, waiting on a surface.
   * Persists across turns within the task; reset only when the task is
   * removed from the store.
   */
  memoryRecalls?: Array<{
    mode: "select" | "synthesize";
    memories: MemoryRecallEntry[];
    receivedAt: number;
  }>;
  /**
   * Set when this task hangs off another — today that means a teammate /
   * subagent lane (see teammateHandlers). Task FORKING, which also wrote this,
   * was removed with the harness arms.
   */
  parentTaskId?: string | null;
  /**
   * The user is drafting a reply in the composer right now (words in the box,
   * the box in focus). Stamped by MessageInput and read only by the desk,
   * which lets Bash listen rather than narrate over someone mid-sentence.
   * Ephemeral — never persisted; a reloaded page has no draft in focus.
   */
  composerTyping?: boolean;
  /**
   * Epoch ms of the last error this client watched land on the task (any class:
   * terminal, recoverable, or a dead session). The error itself is already
   * rendered; what this carries is WHEN, so the desk can say "trying again" for
   * one beat on a turn that is still running instead of its family's verb.
   * Stamped by stores/deskSignals.ts, read only by the desk.
   * Ephemeral — never persisted; a miss is not a property of the task.
   */
  lastErrorAt?: number;
  /**
   * P5.4 (Agent Teams) — present ONLY on hidden teammate TaskStates. Its
   * presence is the single client-side "this is a teammate lane" signal:
   * list surfaces (dashboard, task filtering) and persistence exclude tasks
   * carrying it.
   */
  teammateMeta?: TeammateMeta;
  /**
   * P5.4 (Agent Teams) — present ONLY on a PARENT (lead) task that spawned
   * teammates: live registry keyed by teammateTaskId. Drives the composer's
   * @name mention source and @name → teammateTaskId send routing.
   */
  teammates?: Record<string, { name: string; toolUseId: string }>;
}

export interface ConnectionState {
  /** Display status as reported by the server. 'loading' = waiting for first server response. */
  status: "loading" | DisplayStatus;
  error?: string;
}

export interface SubagentActivity {
  id: string;
  type: "started" | "tool_use" | "completed" | "error";
  agentSlug: string;
  agentType?: string;
  toolName?: string;
  detail?: string;
  timestamp: Date;
  duration?: number;
}

// =============================================================================
// Persisted Types
// =============================================================================

export interface PersistedSession {
  id: string;
  userId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export type PersistedLifecycleState =
  | "scheduled"
  | "queued"
  | "idle"
  | "processing"
  | "awaiting"
  | "error"
  | "completed";

/** @bash/shared is the canonical source for pending detail shapes. */
export type PersistedPendingDetails = PendingDetails;

export interface PersistedTask {
  id: string;
  userId: string;
  agentSlug: string;
  /** Harness runtime (engine) this task is locked to — claude | openai. */
  runtime?: HarnessId;
  /**
   * Pinned model id this task is locked to (e.g. claude-opus-4-8). Serialized by
   * the server (serializeTask) so the composer pill shows the REAL model on a
   * sidebar/deep-link open without waiting for MESSAGE_SYNC. Absent for legacy
   * rows that predate the model column.
   */
  model?: string;
  /** Pulse-lane one-liner ("what's happening") for the home working-now section. */
  pulseHeadline?: string | null;
  /** Done rail verdict: how the task rests ('delivered' | 'needs_user'); NULL pre-verdict. */
  restingState?: string | null;
  /** Done rail verdict: the heart of the final answer — the shelf quote-card text. */
  answerExcerpt?: string | null;
  /** Output doc: the answer as typed blocks (shared TaskOutputDoc). */
  outputDoc?: TaskOutputDoc | null;
  /** Story spine snapshot (task-story.service): latest closed-vocabulary phase;
   *  NULL pre-spine — every surface falls back to its legacy ladder. */
  storyPhase?: string | null;
  /** Story spine snapshot: current step's user-facing line + app slug. */
  headline?: string | null;
  headlineApp?: string | null;
  /** Effective access level (permissions v2) serialized by the server; NULL = inherit. */
  accessLevel?: AccessLevel | null;
  /** Durable live-preview pointer (tasks.live_preview); NULL = no app on the
   *  reserved port. Lets a sidebar/deep-link open show the App tab before
   *  MESSAGE_SYNC lands, same as `model` above. */
  livePreview?: LivePreviewPointer | null;
  /** Agents first (1c): the durable agent this task runs as; null = Bash. Lets a
   *  sidebar open draw the right face at once, same as `model` above. */
  agentId?: string | null;
  /** R-A: the door this task was born through
   *  ('telegram' | 'whatsapp' | 'imessage');
   *  absent = app-born. Drives the channel mark on the board's task lines. */
  originChannel?: OriginChannel;
  parentSessionId?: string;
  title?: string;
  routineId?: string;
  status: string;
  lifecycleState?: PersistedLifecycleState;
  /** Phase 9: pending_details column removed. Non-error awaiting states come from interactions table. */
  pendingDetails?: PersistedPendingDetails;
  /** Phase 9: typed flat columns replace JSONB error shape */
  errorMessage?: string;
  errorCode?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Canonical sort key for the task list. Bumped only on user-meaningful events
   * (message INSERT, lifecycle → completed/awaiting/error) by Postgres triggers.
   * Use this for sorting — `updatedAt` bumps on every internal write (token
   * rollups, sweepers, lifecycle flips) and is not a stable activity signal.
   */
  lastActivityAt: string;
  outputs?: TaskOutput[];
  todos?: TaskTodoGroup[];
  /** Present when this task hangs off another (teammate / subagent lane). */
  parentTaskId?: string | null;
  /**
   * Spec 2026-09-12 — a TEAMMATE of this task is waiting on the person. Derived by the
   * server on every list read, never stored: the lead's own lifecycle says what the LEAD
   * is doing, and a lead that finished its turn reads `idle` while its team works on.
   */
  laneNeedsYou?: boolean;
  /** Engines T5 (tasks.resume_at, ISO): paused on a used-up plan until then; null = not waiting. */
  resumeAt?: string | null;
  /** Several accounts (tasks.engine_slot_id): the slot of the task's last Claude Code turn. */
  engineSlotId?: string | null;
}

export interface TaskStats {
  total: number;
  byStatus: {
    active: number;
    paused: number;
    destroyed: number;
  };
  byLifecycle: {
    queued: number;
    working: number;
    needsInput: number;
    completed: number;
  };
  /** Bash home Activity card: ISO last-activity of every completed task inside
   *  the requested `heatDays` window. Present only when the read asked for it. */
  doneAt?: string[];
  /** Bash home Activity card: all-time count under the same predicate as `doneAt`.
   *  Use this for "N tasks done" — NOT `byLifecycle.completed`, which is derived
   *  server-side and also counts idle rows. Present only when asked. */
  doneTotal?: number;
  /** Present (true) only when the server's doneAt window hit its row cap — the
   *  earliest weeks of the heatmap are under-counted, and the card says so. */
  doneAtTruncated?: true;
}

export interface TaskQueryFilters {
  search?: string;
  dateFilter?: DashboardDateFilter;
  status?: "active" | "paused" | "all";
  statusFilter?: DashboardStatusFilter;
}

export interface SerializedTaskState {
  taskId: string;
  messages: Array<Omit<Message, "timestamp"> & { timestamp: string }>;
  phase: TaskPhase;
  title: string;
  createdAt: string;
  lastActivityAt: string;
  agentSlug: string;
  /** Locked engine — persisted so the read-only selector survives a refresh. */
  runtime?: HarnessId;
  /** Pinned model id (§4.7) — persisted so the read-only selector survives a refresh. */
  model?: string;
  outputs?: TaskOutput[];
  todos?: TaskTodoGroup[];
  promptSuggestion?: string | null;
  tokenUsage?: TokenUsage;
  contextUsage?: ContextUsageSnapshot;
}

/**
 * The task's token counts, from AGENT_USAGE.
 *
 * `input` + `output` are what the desk, the float's Usage line, Working now and the completion
 * footer show: the latest event's full prompt (fresh + cache written + cache read) and every
 * reply. `taskInputTotal` / `taskCacheReadTotal` are the task menu's Usage row alone: every
 * prompt summed, so it covers the same span as `output`, and the cache reads inside it. Context
 * fullness is none of these: that is `contextUsage`, the engine's own reading.
 */
export interface TokenUsage {
  input: number;
  output: number;
  /** Every prompt of the task in full. Absent on tasks persisted before it was counted. */
  taskInputTotal?: number;
  /** The cache-read part of `taskInputTotal`. */
  taskCacheReadTotal?: number;
}

export interface StagedMessage {
  /** Client-minted uuid — the idempotent join key for pickup/cancel/resync. */
  uuid: string;
  content: string;
  taskId: string;
  origin?: import("@bash/shared").MessageOrigin;
  /** Two-brain P2: surface the send was born from ('web' | 'native' | 'channel'). */
  surface?: "web" | "native" | "channel";
  /** Queue lane — 'now' steers the current turn; 'next'/'later'/absent are normal FIFO. */
  priority?: import("@bash/shared").MessagePriority;
  stagedAt: number;
  /**
   * The exact in-flight Message this card represents — stashed at send time for an
   * OWN busy send so MESSAGE_PICKED_UP promotes the original object (with its
   * reconciled identity) into the thread. Absent for cards this client did not
   * originate (a MESSAGE_STAGED echo, or a STAGING_SYNC snapshot after reconnect);
   * pickup synthesizes a minimal user message from the card fields in that case.
   *
   * Folding the Message onto the card (rather than a parallel uuid→Message Map)
   * makes "remove the card" the SINGLE teardown path — no shadow record can orphan.
   */
  message?: Message;
}

/**
 * Phase 3 intelligent ingest — a "saved to your brain" chip recorded when the
 * server acks a deposit (DEPOSIT_ACK). Server-authoritative and idempotent by
 * uuid (the client-minted envelope uuid echoed back). Cleared on a timeout.
 * Only ever populated when NEXT_PUBLIC_ENABLE_INTELLIGENT_INGEST is on.
 */
export interface DepositChip {
  /** Client-minted envelope uuid echoed back — the idempotent dedupe key. */
  uuid?: string;
  /** Short human-readable label describing the deposited content. */
  label: string;
  taskId: string;
  depositedAt: number;
}

export interface TurnSummary {
  durationMs: number;
  tokens: { input: number; output: number };
}

/** @deprecated Use DisplayStatus from @bash/shared directly. Kept as alias during migration. */
export type AgentDisplayStatus = DisplayStatus;

export type DashboardDateFilter = "today" | "week" | "month" | "all";
export type DashboardStatusFilter =
  "all" | "queued" | "active" | "completed" | "attention";

// Re-export external types used by handlers
export type {
  ContentBlock,
  QuestionBlock,
  ImageAttachment,
  ToolUseBlock,
  TodoBlock,
  Todo,
  ToolSummary,
  TaskOutput,
  TaskTodoGroup,
  DisplayStatus,
};
